/**
 * <biosphere-article-toc> — builds the contents list from the article's own
 * headings and marks the one being read. Rendered hidden, revealed here.
 *
 * Marks the last heading past the reading line, not the one on screen, so a
 * section stays marked while you read it. Measured on scroll, not with an
 * observer: an observer reports only entering and leaving its root, so it misses
 * a heading crossing a line inside it, and misses a jump past several at once.
 */
if (!customElements.get("biosphere-article-toc")) {
  customElements.define(
    "biosphere-article-toc",
    class BiosphereArticleToc extends HTMLElement {
      #controller = new AbortController();
      #links = new Map();
      #headings = [];
      #offset = 0;
      #frame = 0;

      connectedCallback() {
        const list = this.querySelector(".biosphere-article-toc__list");
        if (!list) return;

        // Every article body on the page, not just this section's: a long article
        // splits across more than one, and the contents list still covers all of it.
        const headings = [
          ...document.querySelectorAll(
            ".biosphere-article__body [data-article-toc-entry]",
          ),
        ].filter((heading) => heading.id && heading.textContent.trim());
        if (headings.length < 2) return;

        for (const heading of headings) {
          const item = document.createElement("li");
          const link = document.createElement("a");
          link.href = `#${heading.id}`;
          link.textContent =
            heading.dataset.tocLabel || heading.textContent.trim();
          item.append(link);
          list.append(item);
          this.#links.set(heading.id, link);
        }

        this.#headings = headings;

        // Where an anchor jump parks a heading. The line never sits above it, so
        // following a link always marks the section it went to.
        this.#offset =
          parseFloat(
            getComputedStyle(document.documentElement).getPropertyValue(
              "--article-anchor-offset",
            ),
          ) || 0;

        addEventListener("scroll", () => this.#schedule(), {
          passive: true,
          signal: this.#controller.signal,
        });

        this.#mark();
        this.hidden = false;
      }

      disconnectedCallback() {
        this.#controller.abort();
        cancelAnimationFrame(this.#frame);
      }

      /** One measurement per painted frame, however many scroll events arrive. */
      #schedule() {
        if (this.#frame) return;
        this.#frame = requestAnimationFrame(() => {
          this.#frame = 0;
          this.#mark();
        });
      }

      /* Read live off the window, so a resize needs no handling of its own. */
      #mark() {
        const line = Math.max(this.#offset, innerHeight * 0.3);

        let current = null;
        for (const heading of this.#headings) {
          if (heading.getBoundingClientRect().top > line) break;
          current = heading.id;
        }

        for (const [id, link] of this.#links) {
          if (id === current) link.setAttribute("aria-current", "true");
          else link.removeAttribute("aria-current");
        }
      }
    },
  );
}
